import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import { CHART_COLORS } from '../constants';
import { getCurrencySymbol } from '../formatters';
import { type CurrencyKey } from '../types';
import styles from './DonutChart.module.scss';

interface DonutDatum {
  name: string;
  value: number;
  color?: string;
}

interface DonutChartProps {
  data: DonutDatum[];
  currency: CurrencyKey;
  size?: number;
}

const DEFAULT_COLORS = [CHART_COLORS.plum, CHART_COLORS.orangeDark, CHART_COLORS.plumLight];

function CustomTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload: DonutDatum; value: number }>;
  currency: CurrencyKey;
}) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const symbol = getCurrencySymbol(currency);
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipLabel}>{item.payload.name}</span>
      <span className={styles.tooltipValue}>
        {symbol}
        {Math.abs(item.value).toLocaleString()}
      </span>
    </div>
  );
}

export default function DonutChart({ data, currency, size = 240 }: DonutChartProps) {
  return (
    <div className={styles.wrapper}>
      <div className={styles.chartRow}>
        <ResponsiveContainer width={size} height={size}>
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              cx="50%"
              cy="50%"
              innerRadius="55%"
              outerRadius="85%"
              paddingAngle={2}
              strokeWidth={0}
            >
              {data.map((entry, index) => (
                <Cell
                  key={entry.name}
                  fill={entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip content={<CustomTooltip currency={currency} />} />
          </PieChart>
        </ResponsiveContainer>
        <div className={styles.legend}>
          {data.map((entry, index) => (
            <div key={entry.name} className={styles.legendItem}>
              <span
                className={styles.legendDot}
                style={{
                  backgroundColor: entry.color || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
                }}
              />
              <span className={styles.legendLabel}>{entry.name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
